import { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Column } from '@material-table/core';
import BlockIcon from '@mui/icons-material/Block';
import DataTable from '../../components/shared/DataTable/DataTable';
import EvaluationRating from '../app-supplier-database/EvaluationRating';
import { formatShortDay } from '../page-supplier-evaluations/reviewFormat';
import {
  BySupplierPage,
  OverviewApiQuery,
  SupplierOverviewRow,
  serviceEvaluations,
} from '../../services/service-evaluations';
import { EvaluationLimits, EvaluationOverviewSort } from '../../util/evaluationContract';
import RowChevron from './RowChevron';
import SupplierOverviewPanel from './SupplierOverviewPanel';
import { OverviewTableProps, SortFields, isPanelOpen, useRemoteOverview } from './remoteTable';
import styles from './app-evaluation-overview.module.scss';

const load = (query: OverviewApiQuery) => serviceEvaluations.getBySupplier(query);
const rowsOf = (page: BySupplierPage) => page.suppliers;
const keyOf = (row: SupplierOverviewRow) => row.supplierId;

const SORT_FIELDS: SortFields = {
  [EvaluationOverviewSort.Name]: 'name',
  [EvaluationOverviewSort.Score]: 'score',
  [EvaluationOverviewSort.Active]: 'activeCount',
  [EvaluationOverviewSort.LastEvaluated]: 'lastEvaluatedDay',
};

const SupplierOverviewTable: FC<OverviewTableProps> = ({
  query,
  canWrite,
  onLoaded,
  onFailed,
  onOpenChange,
  onPageChange,
  onPageRestored,
  onSortChange,
  onStart,
}) => {
  const [t, i18n] = useTranslation('evaluations');

  const renderPanel = useCallback(
    ({ rowData }: { rowData: SupplierOverviewRow }) => (
      <SupplierOverviewPanel
        row={rowData}
        canWrite={canWrite}
        onStart={onStart}
        onAnswerHidden={() => reload()}
      />
    ),
    [canWrite, onStart],
  );

  const { tableRef, fetchRows, onRowClick, onOrderChange, header, totalCount, reload } =
    useRemoteOverview({
      view: 'supplier',
      query,
      load,
      rowsOf,
      keyOf,
      renderPanel,
      onLoaded: (page, loadedQuery) =>
        onLoaded(
          {
            counts: page.counts,
            batch: page.batch,
            focus: page.focus,
            totalCount: page.totalCount,
          },
          loadedQuery,
        ),
      onFailed,
      onOpenChange,
      onPageRestored,
      onSortChange,
      sortFields: SORT_FIELDS,
    });

  const columns: Column<SupplierOverviewRow>[] = useMemo(
    () => [
      {
        title: '',
        width: 40,
        sorting: false,
        render: row => <RowChevron open={isPanelOpen(row)} label={row.name} />,
      },
      {
        title: t('overview.columns.supplier', 'Leverantör'),
        field: 'name',
        render: row => (
          <span className={styles.supplierName}>
            <span className={styles.name} title={row.name}>
              {row.name}
            </span>
            {row.isBlocked && (
              <BlockIcon
                className={styles.blocked}
                style={{ fontSize: 16 }}
                titleAccess={t('overview.supplier.blocked', 'Spärrad')}
                data-blocked=""
              />
            )}
          </span>
        ),
      },
      {
        title: t('overview.columns.score', 'Betyg'),
        field: 'score',
        width: 140,
        render: row => (
          <EvaluationRating rating={row.score} evaluationCount={row.evaluationCount} />
        ),
      },
      {
        title: t('overview.columns.ongoing', 'Pågående'),
        field: 'activeCount',
        width: 180,
        render: row =>
          row.activeCount
            ? t('overview.group.active', {
                count: row.activeCount,
                defaultValue_one: '{{count}} pågående',
                defaultValue_other: '{{count}} pågående',
              })
            : t('overview.supplier.noActive', 'Inga pågående'),
      },
      {
        title: t('overview.columns.lastEvaluated', 'Senast utvärderad'),
        field: 'lastEvaluatedDay',
        width: 180,
        render: row => (
          <span className={styles.tabular}>
            {row.lastEvaluatedDay ? formatShortDay(row.lastEvaluatedDay, i18n.language) : '–'}
          </span>
        ),
      },
    ],
    [t, i18n.language],
  );

  const sortField = header.sort && SORT_FIELDS[header.sort.key];
  const sortColumn = sortField ? columns.findIndex(column => column.field === sortField) : -1;

  return (
    <DataTable<SupplierOverviewRow>
      key={header.mount}
      tableRef={tableRef}
      columns={columns}
      data={fetchRows}
      isLoading={false}
      defaultOrderBy={sortColumn}
      defaultOrderDirection={header.sort?.dir}
      onOrderChange={onOrderChange}
      pageSize={EvaluationLimits.OverviewPageSize}
      tableLayout="fixed"
      onRowClick={onRowClick}
      onPageChange={page => onPageChange(page + 1)}
      // DataTable's empty-state guard only inspects an array `data`, so a remote table withholds the panel itself.
      detailPanel={totalCount > 0 ? renderPanel : undefined}
    />
  );
};

export default SupplierOverviewTable;
